import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stack,
  IconButton,
  Chip,
  Grid,
  Paper,
  Tooltip,
  Divider,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fade,
  Alert,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  Calculate as CalculateIcon,
  GridOn as GridIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  RestartAlt as ResetIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IcoporSheet {
  id: number;
  name: string;
  length: number;
  width: number;
  unit: string;
}

interface SheetPlacement {
  row: number;
  col: number;
  isHorizontal: boolean;
  usedLength: number;
  usedWidth: number;
  isPartial: boolean;
}

interface CalculationResult {
  sheetsHorizontal: number;
  sheetsVertical: number;
  totalSheets: number;
  totalArea: number;
  wastedArea: number;
  wastedPercentage: number;
  placements: SheetPlacement[];
  sheetLength: number;  // effective sheet length used for drawing
  sheetWidth: number;   // effective sheet width used for drawing
}

// ─── Default sheets ───────────────────────────────────────────────────────────

const DEFAULT_SHEETS: IcoporSheet[] = [
  { id: 1, name: 'Estándar 1.2×0.6', length: 1.2, width: 0.6, unit: 'm' },
  { id: 2, name: 'Grande 2.4×1.2', length: 2.4, width: 1.2, unit: 'm' },
  { id: 3, name: 'Pequeña 1.0×0.5', length: 1.0, width: 0.5, unit: 'm' },
];

// ─── Calculator logic ─────────────────────────────────────────────────────────

function calculateLayout(
  roomLength: number,
  roomWidth: number,
  sheet: IcoporSheet,
  tolerancePct: number
): CalculationResult {
  const rl = roomLength * (1 + tolerancePct / 100);
  const rw = roomWidth * (1 + tolerancePct / 100);

  const calcOrientation = (sx: number, sy: number) => {
    const nx = Math.ceil(rl / sx);
    const ny = Math.ceil(rw / sy);
    const totalSheetArea = nx * sx * ny * sy;
    const roomArea = rl * rw;
    const wastedArea = Math.max(0, totalSheetArea - roomArea);
    return { nx, ny, total: nx * ny, wastedArea, roomArea, sx, sy };
  };

  const h = calcOrientation(sheet.length, sheet.width);
  const v = calcOrientation(sheet.width, sheet.length);
  const best = h.wastedArea <= v.wastedArea ? h : v;

  const placements: SheetPlacement[] = [];
  for (let row = 0; row < best.ny; row++) {
    for (let col = 0; col < best.nx; col++) {
      const xPos = col * best.sx;
      const yPos = row * best.sy;
      const remLen = Math.max(0, rl - xPos);
      const remWid = Math.max(0, rw - yPos);
      placements.push({
        row,
        col,
        isHorizontal: best === h,
        usedLength: Math.min(best.sx, remLen),
        usedWidth: Math.min(best.sy, remWid),
        isPartial:
          (remLen < best.sx && remLen > 0) ||
          (remWid < best.sy && remWid > 0),
      });
    }
  }

  return {
    sheetsHorizontal: best.nx,
    sheetsVertical: best.ny,
    totalSheets: best.total,
    totalArea: best.roomArea,
    wastedArea: best.wastedArea,
    wastedPercentage: (best.wastedArea / (best.total * best.sx * best.sy)) * 100,
    placements,
    sheetLength: best.sx,   // dimensión horizontal efectiva (eje X del canvas)
    sheetWidth: best.sy,    // dimensión vertical efectiva (eje Y del canvas)
  };
}

// ─── Canvas Visualizer ────────────────────────────────────────────────────────

const LayoutCanvas: React.FC<{
  result: CalculationResult;
  roomLength: number;
  roomWidth: number;
}> = ({ result, roomLength, roomWidth }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !result.placements.length) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const PAD = 32;

    ctx.clearRect(0, 0, W, H);

    // Dimensiones totales del grid de láminas
    const totalW = result.sheetsHorizontal * result.sheetLength;
    const totalH = result.sheetsVertical * result.sheetWidth;

    const scaleX = (W - PAD * 2) / totalW;
    const scaleY = (H - PAD * 2) / totalH;
    const scale = Math.min(scaleX, scaleY);

    const offsetX = PAD + ((W - PAD * 2) - totalW * scale) / 2;
    const offsetY = PAD + ((H - PAD * 2) - totalH * scale) / 2;

    // Fondo
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(offsetX, offsetY, totalW * scale, totalH * scale);

    for (const p of result.placements) {
      const x = offsetX + p.col * result.sheetLength * scale;
      const y = offsetY + p.row * result.sheetWidth * scale;
      const fw = result.sheetLength * scale;
      const fh = result.sheetWidth * scale;
      const uw = p.usedLength * scale;
      const uh = p.usedWidth * scale;

      ctx.fillStyle = p.isPartial ? '#86efac' : '#4ade80';
      ctx.fillRect(x, y, uw, uh);

      if (uw < fw) {
        ctx.fillStyle = '#fca5a5';
        ctx.fillRect(x + uw, y, fw - uw, fh);  // columna derecha: desperdicio de todo el alto
      }
      if (uh < fh) {
        ctx.fillStyle = '#fca5a5';
        ctx.fillRect(x, y + uh, uw, fh - uh);  // fila inferior: solo hasta uw, no fw
      }

      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x, y, fw, fh);

      if (fw > 28 && fh > 18) {
        ctx.fillStyle = '#1e293b';
        ctx.font = `bold ${Math.min(10, fw / 4)}px monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${p.col + 1}×${p.row + 1}`, x + fw / 2, y + fh / 2);
      }
    }

    // ✅ Borde del área real del cuarto — cálculo corregido
    const roomW = roomLength * scale;   // ← era la línea rota
    const roomH = roomWidth * scale;

    ctx.strokeStyle = '#1d4ed8';
    ctx.lineWidth = 2.5;
    ctx.setLineDash([6, 3]);
    ctx.strokeRect(offsetX, offsetY, roomW, roomH);
    ctx.setLineDash([]);

  }, [result, roomLength, roomWidth]);

  return (
    <canvas
      ref={canvasRef}
      width={560}
      height={320}
      style={{
        width: '100%',
        height: 'auto',
        minHeight: 300,   // ← agrega esto
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        background: 'white',
        display: 'block',
      }}
    />
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

interface IcoporCalculatorModalProps {
  open: boolean;
  onClose: () => void;
}

const IcoporCalculatorModal: React.FC<IcoporCalculatorModalProps> = ({ open, onClose }) => {
  const [sheets, setSheets] = useState<IcoporSheet[]>(DEFAULT_SHEETS);
  const [selectedSheetId, setSelectedSheetId] = useState<number>(1);
  const [roomLength, setRoomLength] = useState('');
  const [roomWidth, setRoomWidth] = useState('');
  const [tolerance, setTolerance] = useState(0);
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [newSheet, setNewSheet] = useState({ name: '', length: '', width: '' });
  const [error, setError] = useState('');

  const selectedSheet = sheets.find((s) => s.id === selectedSheetId) ?? sheets[0];

  const efficiency = result ? 100 - result.wastedPercentage : null;
  const efficiencyColor =
    efficiency === null ? 'default' :
      efficiency >= 90 ? 'success' :
        efficiency >= 75 ? 'warning' : 'error';
  const efficiencyLabel =
    efficiency === null ? '' :
      efficiency >= 90 ? 'Excelente' :
        efficiency >= 75 ? 'Aceptable' : 'Mejorable';

  const handleCalculate = () => {
    setError('');
    const rl = parseFloat(roomLength);
    const rw = parseFloat(roomWidth);
    if (!rl || !rw || rl <= 0 || rw <= 0) {
      setError('Ingresa dimensiones válidas mayores a 0.');
      return;
    }
    if (!selectedSheet) return;
    setResult(calculateLayout(rl, rw, selectedSheet, tolerance));
  };

  const handleReset = () => {
    setRoomLength('');
    setRoomWidth('');
    setTolerance(0);
    setResult(null);
    setError('');
  };

  const handleAddSheet = () => {
    const l = parseFloat(newSheet.length);
    const w = parseFloat(newSheet.width);
    if (!newSheet.name || !l || !w || l <= 0 || w <= 0) return;
    const id = Date.now();
    setSheets((prev) => [...prev, { id, name: newSheet.name, length: l, width: w, unit: 'm' }]);
    setSelectedSheetId(id);
    setNewSheet({ name: '', length: '', width: '' });
    setShowAddSheet(false);
  };

  const handleDeleteSheet = (id: number) => {
    setSheets((prev) => prev.filter((s) => s.id !== id));
    if (selectedSheetId === id) setSelectedSheetId(sheets.find((s) => s.id !== id)?.id ?? sheets[0].id);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: '#f8fafc',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: 3, py: 2.5,
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <GridIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} color="white" lineHeight={1.2}>
                Calculadora de Icopor
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Planificación óptima de láminas
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={2.5}>

          {/* ── LEFT: Inputs ── */}
          <Grid item xs={12} md={5}>
            <Stack spacing={2}>

              {/* Dimensions */}
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 2 }}>
                  Dimensiones del espacio
                </Typography>
                <Stack spacing={1.5}>
                  <TextField
                    label="Largo (m)"
                    value={roomLength}
                    onChange={(e) => setRoomLength(e.target.value)}
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    label="Ancho (m)"
                    value={roomWidth}
                    onChange={(e) => setRoomWidth(e.target.value)}
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Stack>
              </Paper>

              {/* Sheet selector */}
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Tipo de lámina</Typography>
                  <Tooltip title="Agregar lámina personalizada">
                    <IconButton size="small" onClick={() => setShowAddSheet((v) => !v)}
                      sx={{ bgcolor: showAddSheet ? 'primary.50' : 'transparent' }}>
                      <AddIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <FormControl fullWidth size="small">
                  <Select
                    value={selectedSheetId}
                    onChange={(e) => setSelectedSheetId(Number(e.target.value))}
                    sx={{ borderRadius: 2 }}
                  >
                    {sheets.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                          <span>{s.name}</span>
                          <Typography variant="caption" color="text.secondary">
                            {s.length}×{s.width} {s.unit}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Collapse in={showAddSheet}>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e2e8f0' }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Nueva lámina personalizada
                    </Typography>
                    <Stack spacing={1}>
                      <TextField
                        placeholder="Nombre (ej: Grande 2×1)"
                        size="small"
                        value={newSheet.name}
                        onChange={(e) => setNewSheet((p) => ({ ...p, name: e.target.value }))}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                      <Stack direction="row" spacing={1}>
                        <TextField
                          placeholder="Largo"
                          size="small"
                          type="number"
                          value={newSheet.length}
                          onChange={(e) => setNewSheet((p) => ({ ...p, length: e.target.value }))}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                        <TextField
                          placeholder="Ancho"
                          size="small"
                          type="number"
                          value={newSheet.width}
                          onChange={(e) => setNewSheet((p) => ({ ...p, width: e.target.value }))}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Stack>
                      <Button size="small" variant="contained" onClick={handleAddSheet}
                        sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                        Agregar
                      </Button>
                    </Stack>
                  </Box>
                </Collapse>

                {sheets.length > DEFAULT_SHEETS.length && (
                  <Box sx={{ mt: 1.5 }}>
                    {sheets.filter((s) => !DEFAULT_SHEETS.find((d) => d.id === s.id)).map((s) => (
                      <Stack key={s.id} direction="row" alignItems="center" justifyContent="space-between"
                        sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: '#f0f9ff', mt: 0.5 }}>
                        <Typography variant="caption">{s.name} — {s.length}×{s.width} m</Typography>
                        <IconButton size="small" onClick={() => handleDeleteSheet(s.id)}>
                          <DeleteIcon sx={{ fontSize: 14, color: 'error.main' }} />
                        </IconButton>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Paper>

              {/* Tolerance */}
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Tolerancia</Typography>
                  <Chip label={`+${tolerance}%`} size="small" color={tolerance > 0 ? 'warning' : 'default'} />
                </Stack>
                <Slider
                  value={tolerance}
                  onChange={(_, v) => setTolerance(v as number)}
                  min={0} max={15} step={1}
                  marks={[{ value: 0, label: '0%' }, { value: 5, label: '5%' }, { value: 10, label: '10%' }, { value: 15, label: '15%' }]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `+${v}%`}
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Margen extra para cortes y ajustes en obra
                </Typography>
              </Paper>

              {error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
              )}

              <Stack direction="row" spacing={1.5}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CalculateIcon />}
                  onClick={handleCalculate}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: 2,
                    py: 1.2,
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
                    boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
                    '&:hover': { boxShadow: '0 6px 20px rgba(37,99,235,0.45)' },
                  }}
                >
                  Calcular
                </Button>
                <Tooltip title="Limpiar">
                  <IconButton onClick={handleReset} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <ResetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

            </Stack>
          </Grid>

          {/* ── RIGHT: Results & Visualization ── */}
          <Grid item xs={12} md={7}>
            {!result ? (
              <Box
                sx={{
                  height: '100%', minHeight: 360,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'white', borderRadius: 2.5, border: '1px dashed #cbd5e1',
                  gap: 1.5,
                }}
              >
                <GridIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
                <Typography variant="body2" color="text.disabled" fontWeight={500}>
                  Ingresa las dimensiones y presiona Calcular
                </Typography>
              </Box>
            ) : (
              <Fade in>
                <Stack spacing={2}>

                  {/* KPI row */}
                  <Grid container spacing={1.5}>
                    {[
                      { label: 'Láminas totales', value: result.totalSheets, unit: 'uds', color: '#1e3a5f' },
                      { label: 'Área cubierta', value: result.totalArea.toFixed(2), unit: 'm²', color: '#0891b2' },
                      { label: 'Desperdicio', value: result.wastedArea.toFixed(2), unit: 'm²', color: '#dc2626' },
                    ].map(({ label, value, unit, color }) => (
                      <Grid item xs={4} key={label}>
                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', textAlign: 'center', bgcolor: 'white' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
                          <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.5} sx={{ mt: 0.5 }}>
                            <Typography variant="h5" fontWeight={800} sx={{ color }}>{value}</Typography>
                            <Typography variant="caption" color="text.secondary">{unit}</Typography>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Efficiency bar */}
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        APROVECHAMIENTO DEL MATERIAL
                      </Typography>
                      <Chip
                        label={`${efficiencyLabel} · ${(100 - result.wastedPercentage).toFixed(1)}%`}
                        size="small"
                        color={efficiencyColor as any}
                        icon={
                          efficiencyColor === 'success' ? <CheckCircleIcon fontSize="small" /> :
                            efficiencyColor === 'warning' ? <WarningIcon fontSize="small" /> :
                              <ErrorIcon fontSize="small" />
                        }
                        sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                      />
                    </Stack>
                    <Box sx={{ height: 10, borderRadius: 5, bgcolor: '#fee2e2', overflow: 'hidden' }}>
                      <Box
                        sx={{
                          height: '100%',
                          width: `${100 - result.wastedPercentage}%`,
                          borderRadius: 5,
                          bgcolor: efficiencyColor === 'success' ? '#22c55e' :
                            efficiencyColor === 'warning' ? '#f59e0b' : '#ef4444',
                          transition: 'width 0.8s cubic-bezier(.4,0,.2,1)',
                        }}
                      />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="success.main" fontWeight={600}>
                        ✓ {(100 - result.wastedPercentage).toFixed(1)}% útil
                      </Typography>
                      <Typography variant="caption" color="error.main" fontWeight={600}>
                        ✗ {result.wastedPercentage.toFixed(1)}% desperdicio
                      </Typography>
                    </Stack>
                  </Paper>

                  {/* Layout info */}
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                    <Stack direction="row" spacing={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Disposición</Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {result.sheetsHorizontal} × {result.sheetsVertical} láminas
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Orientación</Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {result.placements[0]?.isHorizontal ? 'Horizontal' : 'Vertical'}
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Lámina efectiva</Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {result.sheetLength}×{result.sheetWidth} m
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  {/* Visualization */}
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        DIAGRAMA DE COLOCACIÓN
                      </Typography>
                      <Tooltip title="Verde = material utilizado · Rojo = desperdicio · Azul punteado = área real del espacio">
                        <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      </Tooltip>
                    </Stack>
                    <LayoutCanvas
                      result={result}
                      roomLength={parseFloat(roomLength)}
                      roomWidth={parseFloat(roomWidth)}
                    />
                    <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                      {[
                        { color: '#4ade80', label: 'Utilizado' },
                        { color: '#fca5a5', label: 'Desperdicio' },
                        { color: '#1d4ed8', label: 'Área real', dashed: true },
                      ].map(({ color, label, dashed }) => (
                        <Stack direction="row" alignItems="center" spacing={0.5} key={label}>
                          <Box sx={{
                            width: 14, height: 14, borderRadius: 0.5, bgcolor: dashed ? 'transparent' : color,
                            border: dashed ? `2px dashed ${color}` : `1.5px solid ${color}`,
                          }} />
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>

                </Stack>
              </Fade>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IcoporCalculatorModal;